'use client';

import { useId, useMemo, useState, useRef } from 'react';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useThemeVersion } from '@/lib/theme/useThemeColors';
import type { GoalSummary } from '@/types';
import { getGoalPaletteColor } from './helpers';

interface Props {
  goals: GoalSummary[];
  totalCommitted: number;
  onPickGoal: (g: GoalSummary) => void;
}

interface Slice {
  goal: GoalSummary;
  value: number;
  pctOf: number;
  start: number;
  end: number;
  color: string;
}

export function AllocationDonut({ goals, totalCommitted, onPickGoal }: Props) {
  const [active, setActive] = useState<string | null>(null);
  const formatCurrency = useFormatCurrency();
  const themeVersion = useThemeVersion();
  const titleId = useId();
  const descId = useId();
  const sliceRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  const slices = useMemo<Slice[]>(() => {
    let acc = 0;
    return goals
      .filter((g) => (g.monthlyContribution ?? 0) > 0)
      .map((g, idx) => {
        const value = g.monthlyContribution ?? 0;
        const pctOf = totalCommitted > 0 ? value / totalCommitted : 0;
        const start = acc;
        acc += pctOf;
        return { goal: g, value, pctOf, start, end: acc, color: getGoalPaletteColor(idx) };
      });
    // themeVersion: getGoalPaletteColor reads CSS tokens at runtime — re-run on a theme flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, totalCommitted, themeVersion]);

  const size = 192;
  const r = 82;
  const inner = 64;
  const cx = size / 2;
  const cy = size / 2;

  const arcPath = (start: number, end: number) => {
    // Full-circle special-case so the single-slice donut renders correctly.
    if (end - start >= 0.9999) {
      return `M ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} M ${cx + inner} ${cy} A ${inner} ${inner} 0 1 0 ${cx - inner} ${cy} A ${inner} ${inner} 0 1 0 ${cx + inner} ${cy} Z`;
    }
    const a0 = start * 2 * Math.PI - Math.PI / 2;
    const a1 = end * 2 * Math.PI - Math.PI / 2;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const xi0 = cx + inner * Math.cos(a0);
    const yi0 = cy + inner * Math.sin(a0);
    const xi1 = cx + inner * Math.cos(a1);
    const yi1 = cy + inner * Math.sin(a1);
    const large = end - start > 0.5 ? 1 : 0;
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
  };

  const activeSlice = slices.find((s) => s.goal.id === active);

  const description = useMemo(() => {
    if (slices.length === 0) return 'Nenhuma meta com aporte mensal cadastrado.';
    const parts = slices.map(
      (s) => `${s.goal.name}: ${formatCurrency(s.value)} por mês, ${Math.round(s.pctOf * 100)}% do total`,
    );
    return `Distribuição do aporte mensal de ${formatCurrency(totalCommitted)}. ${parts.join('. ')}.`;
  }, [slices, totalCommitted, formatCurrency]);

  const focusSliceAt = (index: number) => {
    const wrapped = ((index % slices.length) + slices.length) % slices.length;
    const id = slices[wrapped]?.goal.id;
    if (!id) return;
    setActive(id);
    sliceRefs.current.get(id)?.focus();
  };

  return (
    <section
      aria-labelledby={titleId}
      className="@container h-full rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)] p-4"
    >
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <div>
          <h3 id={titleId} className="text-sm font-semibold text-text-primary">
            Alocação mensal
          </h3>
          <p className="mt-0.5 text-xs text-text-tertiary">Por meta</p>
        </div>
      </header>

      {slices.length === 0 ? (
        <p className="py-12 text-center text-sm text-text-tertiary">
          Sem aportes mensais cadastrados
        </p>
      ) : (
        <div className="flex flex-col items-center gap-5 @sm:flex-row">
          <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg
              width={size}
              height={size}
              role="img"
              aria-labelledby={titleId}
              aria-describedby={descId}
              focusable="false"
            >
              <desc id={descId}>{description}</desc>
              <g>
                {slices.map((s, idx) => {
                  const isActive = active === s.goal.id;
                  const dim = active !== null && !isActive;
                  return (
                    <path
                      key={s.goal.id}
                      d={arcPath(s.start, s.end)}
                      fill={s.color}
                      opacity={dim ? 0.28 : 1}
                      stroke="#ffffff"
                      strokeWidth={2}
                      style={{
                        transition: 'opacity 180ms',
                      }}
                      className="pointer-events-none motion-reduce:transition-none"
                      aria-hidden="true"
                      data-index={idx}
                    />
                  );
                })}
              </g>
            </svg>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
            >
              <div className="font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
                Total/mês
              </div>
              <div className="mt-0.5 font-sans text-base font-bold text-text-primary tabular-nums sm:text-lg">
                {formatCurrency(activeSlice ? activeSlice.value : totalCommitted)}
              </div>
              <div className="mt-0.5 min-h-[14px] font-mono text-2xs text-text-secondary tabular-nums">
                {activeSlice ? `${Math.round(activeSlice.pctOf * 100)}% • ${activeSlice.goal.name}` : ''}
              </div>
            </div>
          </div>

          <ul role="list" className="w-full min-w-0 flex-1 space-y-1">
            {slices.map((s, idx) => {
              const isActive = active === s.goal.id;
              return (
                <li key={s.goal.id}>
                  <button
                    ref={(el) => {
                      sliceRefs.current.set(s.goal.id, el);
                    }}
                    type="button"
                    onClick={() => onPickGoal(s.goal)}
                    onFocus={() => setActive(s.goal.id)}
                    onBlur={() => setActive((v) => (v === s.goal.id ? null : v))}
                    onMouseEnter={() => setActive(s.goal.id)}
                    onMouseLeave={() => setActive((v) => (v === s.goal.id ? null : v))}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                        e.preventDefault();
                        focusSliceAt(idx + 1);
                      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                        e.preventDefault();
                        focusSliceAt(idx - 1);
                      } else if (e.key === 'Home') {
                        e.preventDefault();
                        focusSliceAt(0);
                      } else if (e.key === 'End') {
                        e.preventDefault();
                        focusSliceAt(slices.length - 1);
                      }
                    }}
                    aria-label={`${s.goal.name}: ${formatCurrency(s.value)} por mês, ${Math.round(s.pctOf * 100)}% do aporte total. Pressione Enter para abrir.`}
                    className={`w-full flex items-center gap-2 text-xs py-1.5 px-2 rounded-md transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none ${
                      isActive ? 'bg-surface-sunken' : 'hover:bg-surface-sunken'
                    }`}
                    style={{ opacity: active !== null && !isActive ? 0.55 : 1 }}
                  >
                    <span
                      className="size-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: s.color }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate text-left text-text-primary">
                      {s.goal.name}
                    </span>
                    <span className="font-mono text-2xs text-text-secondary tabular-nums">
                      {Math.round(s.pctOf * 100)}%
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
